import { useContext, useEffect } from 'react';
import {
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  FormGroup,
  Input,
  Label,
  UncontrolledDropdown,
} from 'reactstrap';
import { useMutation } from '@apollo/client';
import { useParams } from 'react-router';
import { captureEvent } from '../../utils/analytics';
import { PanelMainGroup, PanelSection } from '../SidePanel';
import { baseLayerTypes } from '../../hooks/useBaseLayer';
import { MapContext } from '../../contexts/MapContext';
import { UpdateFarmMutation } from '../../mutations';
import { useFarmUpdatedAt } from '../../utils/hooks';
import { showError, showSuccess } from '../../utils/toast';
import GearIcon from '../icons/GearIcon';

export const BASE_LAYER_ID = 'base';

const baseLayerLabels = {
  [baseLayerTypes.NAIP_SATELLITE]: 'NAIP US Satellite',
  [baseLayerTypes.MAPBOX_SATELLITE]: 'Mapbox Global Satellite',
  [baseLayerTypes.AZURE_SATELLITE]: 'Azure Global Satellite',
  [baseLayerTypes.NO_SATELLITE]: 'No Satellite',
};

const BaseLayerMenu = ({ readonly }) => {
  const { farmId } = useParams();
  const updatedAt = useFarmUpdatedAt();
  const [updateFarm] = useMutation(UpdateFarmMutation);

  const {
    baseMapState: {
      baseLayer,
      contour,
      roads,
      dark,
      setBaseLayer,
      resetBaseLayer,
      toggleContour,
      toggleRoads,
      toggleDark,
    },
  } = useContext(MapContext);

  // Save current settings as default for this farm
  const saveAsDefault = () => {
    if (!farmId || !updatedAt) return;

    updateFarm({
      variables: {
        input: {
          id: farmId,
          updatedAt,
          defaultBaseMap: baseLayer,
          baseLayerContoursEnabled: contour,
          baseLayerRoadsEnabled: roads,
          baseLayerDarkEnabled: dark,
        },
      },
      onCompleted: () => {
        showSuccess('Base layer settings saved as default');
        captureEvent('base-layer-menu-save-as-default');
      },
      onError: (error) => {
        showError(`Error saving defaults: ${error.message}`);
      },
    });
  };

  useEffect(() => {
    captureEvent('base-layer-menu-menu-opened');
  }, []);

  return (
    <PanelMainGroup>
      <PanelSection>
        <div className="d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Base Map</h4>
          {readonly ? (
            <div /> /* Placeholder for readonly state, no actions available */
          ) : (
            <UncontrolledDropdown>
              <DropdownToggle caret color="link" className="p-0">
                <GearIcon />
              </DropdownToggle>
              <DropdownMenu right size="sm">
                <DropdownItem onClick={saveAsDefault}>
                  Save as Farm Default
                </DropdownItem>
                <DropdownItem onClick={resetBaseLayer}>
                  Restore Farm Defaults
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          )}
        </div>
        <FormGroup tag="fieldset" className="mb-0">
          {Object.values(baseLayerTypes).map((layer) => {
            const id = `baselayer-checkbox-${layer}`;
            return (
              <FormGroup
                check
                className="d-flex align-items-center my-2"
                key={id}
              >
                <Input
                  name="baselayer"
                  type="radio"
                  className="mt-0"
                  checked={baseLayer === layer}
                  onChange={() => {
                    captureEvent('base-layer-menu-base-layer-updated', {
                      layer,
                    });
                    setBaseLayer(layer);
                  }}
                  id={id}
                />
                <Label check htmlFor={id}>
                  {baseLayerLabels[layer]}
                </Label>
              </FormGroup>
            );
          })}
        </FormGroup>
      </PanelSection>
      <PanelSection>
        <h4>10m Contour Lines</h4>
        <FormGroup check className="d-flex align-items-center my-2">
          <Input
            type="checkbox"
            name="contour"
            className="mb-1"
            checked={contour}
            onChange={() => {
              captureEvent('base-layer-menu-contours-toggled');
              toggleContour();
            }}
            id="contour-checkbox"
          />
          <Label check htmlFor="contour-checkbox">
            Show Contour Lines
          </Label>
        </FormGroup>
      </PanelSection>
      <PanelSection>
        <h4>Roads</h4>
        <FormGroup check className="d-flex align-items-center my-2">
          <Input
            type="checkbox"
            name="roads"
            className="mb-1"
            checked={roads}
            onChange={() => {
              captureEvent('base-layer-menu-roads-toggled');
              toggleRoads();
            }}
            id="road-checkbox"
          />
          <Label check htmlFor="road-checkbox">
            Show roads
          </Label>
        </FormGroup>
      </PanelSection>
      {baseLayer === baseLayerTypes.NO_SATELLITE && (
        <PanelSection>
          <h4>Background</h4>
          <FormGroup check className="d-flex align-items-center my-2">
            <Input
              type="checkbox"
              name="dark"
              className="mb-1"
              checked={dark}
              onChange={() => {
                captureEvent('base-layer-dark-mode-toggled');
                toggleDark();
              }}
              id="dark-checkbox"
            />
            <Label check htmlFor="dark-checkbox">
              Darken background
            </Label>
          </FormGroup>
        </PanelSection>
      )}
    </PanelMainGroup>
  );
};

export default BaseLayerMenu;
